import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { apiCall, clearAuthToken, setActiveHospitalId, getActiveHospitalId } from '../utils/ApiUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [activeHospitalId, setActiveHospitalState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Dirty-form guard: AppContent registers a callback (via setBeforeSwitch)
  // that returns false when the user declines to discard unsaved changes.
  // switchHospital/logout await it and abort when it returns false.
  const beforeSwitchRef = useRef(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // A 401 from ANY apiCall means the session died mid-use (cookie expired, user
  // deactivated, password changed, or the row deleted). apiCall clears local
  // session state and broadcasts; drop the user here so the app renders Login
  // instead of an authenticated-looking shell whose every action fails.
  // Deliberately does NOT go through logout() — that would run the dirty-form
  // guard and prompt about unsaved changes during a forced logout.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      clearAuthToken();
      setHospitals([]);
      setActiveHospitalState(null);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      // The session cookie is httpOnly, so the browser cannot inspect it from
      // JavaScript. Always ask the server who the current user is; an expired
      // or missing cookie returns 401 and the catch below renders Login.
      const response = await apiCall('/auth/me');
      if (response.user) {
        setUser(response.user);
        // If global admin (hospitalId null), fetch hospitals list and restore
        // the previously selected hospital — fetchHospitals validates the saved
        // id against the list (deleted/deactivated hospitals are dropped).
        if (response.user.hospitalId === null || response.user.hospitalId === undefined) {
          await fetchHospitals();
        } else {
          // Per-hospital user: their hospitalId is fixed
          setActiveHospitalState(response.user.hospitalId);
        }
      }
    } catch (err) {
      // A 401 means the session is genuinely dead — drop it. Any other failure
      // (server restarting, timeout, DNS) is transient: clearing the token here
      // logged users out for a blip and discarded a still-valid session, since
      // the token itself was never rejected.
      if (err?.status === 401) {
        setUser(null);
        clearAuthToken();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHospitals = async () => {
    try {
      const list = await apiCall('/hospitals');
      setHospitals(list || []);
      // Restore the saved selection only when that hospital still exists and
      // is active — a deleted/deactivated hospital would 400 every request
      // and lock the user on the error screen. A stale saved id is dropped.
      const saved = getActiveHospitalId();
      const activeList = (list || []).filter((h) => h.active !== false);
      if (saved && activeList.some((h) => h.id === saved)) {
        setActiveHospitalState(saved);
      } else {
        if (saved) setActiveHospitalId(null);
        // Auto-select the first active hospital so the global admin can
        // immediately work with data instead of getting 400 errors on every
        // write. They can still switch to "All Hospitals" or another hospital
        // via the dropdown.
        if (activeList.length > 0 && activeList[0].id) {
          setActiveHospitalId(activeList[0].id);
          setActiveHospitalState(activeList[0].id);
        } else {
          setActiveHospitalState(null);
        }
      }
    } catch (err) {
      // Not global admin or error — ignore
    }
  };

  const login = async (username, password, rememberMe = false) => {
    try {
      setError(null);
      setLoading(true);
      const response = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password, rememberMe }),
      });

      if (response.user) {
        setUser(response.user);
        // Mark a fresh login so AppContent resets to the default tab once —
        // a plain page refresh must NOT reset the persisted tab.
        try { sessionStorage.setItem('lab_fresh_login', '1'); } catch {}
        // Global admin: fetch hospitals list, restore saved selection
        // (validated — stale/deleted hospitals are dropped by fetchHospitals)
        if (response.user.hospitalId === null || response.user.hospitalId === undefined) {
          await fetchHospitals();
        } else {
          setActiveHospitalState(response.user.hospitalId);
        }
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Dirty-form guard — abort when the user declines to discard changes
    const guard = beforeSwitchRef.current;
    if (guard) {
      const ok = await guard();
      if (ok === false) return false;
    }
    try {
      await apiCall('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Ignore logout errors
    } finally {
      setUser(null);
      setHospitals([]);
      setActiveHospitalState(null);
      clearAuthToken();
    }
    return true;
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await apiCall('/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      return { success: true, message: response.message };
    } catch (err) {
      const errorMessage = err.message || 'Failed to change password';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Registers the dirty-form guard (AppContent). Stored in a ref so
  // switchHospital/logout stay referentially stable.
  const setBeforeSwitch = useCallback((fn) => {
    beforeSwitchRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  // Global admin switches the active hospital. Sets the header for all
  // subsequent apiCalls and triggers a data refetch in AppContent. Awaits the
  // dirty-form guard first — returns false (and does NOT switch) when the
  // user declines to discard unsaved changes.
  const switchHospital = useCallback(async (hospitalId) => {
    const guard = beforeSwitchRef.current;
    if (guard) {
      const ok = await guard();
      if (ok === false) return false;
    }
    setActiveHospitalId(hospitalId);
    setActiveHospitalState(hospitalId);
    return true;
  }, []);

  const value = {
    user,
    hospitals,
    activeHospitalId,
    loading,
    error,
    login,
    logout,
    changePassword,
    checkAuth,
    switchHospital,
    setBeforeSwitch,
    refreshHospitals: fetchHospitals,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isLabTech: user?.role === 'lab_tech' || user?.role === 'admin',
    isViewer: !!user,
    // Global admin = role is admin AND hospitalId is null (sees all hospitals
    // via selector). The role check is a defensive guard: a non-admin user
    // (lab_tech/viewer) should never have hospital_id NULL, but if one does
    // exist (legacy bad data), it must NOT get global-admin powers.
    isGlobalAdmin: !!user && user.role === 'admin' && (user.hospitalId === null || user.hospitalId === undefined),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
